import React, { useEffect, useState } from "react";
import { Row, Col, Card, Typography, Button, Spin } from "antd";
import { CloseCircleOutlined, CalendarOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { axiosInstant } from "../../api";

interface ActionSummary {
  rejected_manager: number;
  shortlisted_manager: number;
  approved_manager: number;
}

interface ActionCard {
  key: string;
  icon: React.ReactNode;
  label: string;
  description: string;
  color: string;
  borderColor: string;
  onView: () => void;
}

interface Props {
  onFilterStatus: (status: string) => void;
}

const RecruiterActionDashboard: React.FC<Props> = ({ onFilterStatus }) => {
  const [summary, setSummary] = useState<ActionSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axiosInstant
      .get("/job-applications/applications/recruiter/action-summary")
      .then((res) => setSummary(res.data))
      .catch(() => setSummary({ rejected_manager: 0, shortlisted_manager: 0, approved_manager: 0 }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spin style={{ display: "block", margin: "32px auto" }} />;
  if (!summary) return null;


  const cards: ActionCard[] = [
    {
      key: "rejected_manager",
      icon: <CloseCircleOutlined style={{ fontSize: 28, color: "#cf1322" }} />,
      label: "Send Rejection",
      description: "Manager-rejected candidates waiting for you to send the final rejection email.",
      color: "#fff1f0",
      borderColor: "#ffa39e",
      onView: () => onFilterStatus("rejected_manager"),
    },
    {
      key: "shortlisted_manager",
      icon: <CalendarOutlined style={{ fontSize: 28, color: "#389e0d" }} />,
      label: "Schedule 2nd Interview",
      description: "Manager-shortlisted candidates waiting for a 2nd interview to be booked.",
      color: "#f6ffed",
      borderColor: "#b7eb8f",
      onView: () => onFilterStatus("shortlisted_manager"),
    },
    {
      key: "approved_manager",
      icon: <CheckCircleOutlined style={{ fontSize: 28, color: "#722ed1" }} />,
      label: "Place Offer",
      description: "Manager-approved candidates waiting for an offer to be placed.",
      color: "#f9f0ff",
      borderColor: "#d3adf7",
      onView: () => onFilterStatus("approved_manager"),
    },
  ];

  return (
    <div style={{ marginBottom: 24 }}>
      <Typography.Title level={4} style={{ marginBottom: 16, color: "#1a1a2e" }}>
        Action Required
      </Typography.Title>
      <Row gutter={[16, 16]}>
        {cards.map((card) => (
          <Col xs={24} sm={12} md={8} key={card.key}>
            <Card
              style={{
                background: card.color,
                border: `1px solid ${card.borderColor}`,
                borderRadius: 12,
                height: "100%",
              }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 10,
                    padding: "10px 12px",
                    flexShrink: 0,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                  }}
                >
                  {card.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
                    <Typography.Text style={{ fontSize: 32, fontWeight: 700, lineHeight: 1 }}>
                      {(summary as any)[card.key]}
                    </Typography.Text>
                    <Typography.Text strong style={{ fontSize: 16 }}>
                      {card.label}
                    </Typography.Text>
                  </div>
                  <Typography.Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 14 }}>
                    {card.description}
                  </Typography.Text>
                  <Button
                    size="small"
                    style={{ borderColor: card.borderColor, fontWeight: 600 }}
                    onClick={card.onView}
                  >
                    View Jobs
                  </Button>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default RecruiterActionDashboard;
